import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiImage,
  FiLink,
  FiUploadCloud,
  FiX,
} from 'react-icons/fi';

const initialForm = {
  category: '교육기관',
  description: '',
  mediaUrl: '',
  organization: '',
  targetUrl: '',
  thumbnailUrl: '',
  title: '',
};

const MediaUploadModal = ({ categories, onClose, onSubmit }) => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => event.key === 'Escape' && onClose();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setError('');
  };

  const handleThumbnail = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      setError('대표 이미지는 1.5MB 이하의 파일을 선택해 주세요.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, thumbnailUrl: String(reader.result || '') }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.organization.trim() || !form.title.trim() || !form.description.trim()) {
      setError('기관명, 제목, 게시글 설명을 모두 입력해 주세요.');
      return;
    }

    const id = window.crypto?.randomUUID?.() || `media-${Date.now()}`;
    onSubmit({
      ...form,
      id,
      organization: form.organization.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      publishedAt: new Date().toISOString().slice(0, 10),
      views: 0,
      duration: form.mediaUrl ? '영상' : '게시물',
      theme: 'violet',
      featured: false,
      verified: false,
      tags: [form.category, '새 소식'],
    });
  };

  return createPortal(
    <div className="ai-media-upload-modal" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="ai-media-upload" onSubmit={handleSubmit}>
        <header>
          <span><FiUploadCloud aria-hidden="true" /></span>
          <div>
            <h2>홍보 콘텐츠 등록</h2>
            <p>교육기관과 강사의 새로운 소식을 등록하세요.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="등록 창 닫기"><FiX aria-hidden="true" /></button>
        </header>

        <div className="ai-media-upload__body">
          <div className="ai-media-upload__fields">
            <label>
              <span>기관·채널명 <em>필수</em></span>
              <input name="organization" value={form.organization} onChange={updateField} placeholder="예: 한빛진로교육원" autoFocus />
            </label>
            <label>
              <span>분류 <em>필수</em></span>
              <select name="category" value={form.category} onChange={updateField}>
                {categories.filter((category) => category !== '전체').map((category) => (
                  <option value={category} key={category}>{category}</option>
                ))}
              </select>
            </label>
          </div>

          <label>
            <span>제목 <em>필수</em></span>
            <input name="title" value={form.title} onChange={updateField} placeholder="홍보 콘텐츠의 제목을 입력하세요." maxLength={80} />
          </label>

          <label>
            <span>게시글 설명 <em>필수</em></span>
            <textarea name="description" value={form.description} onChange={updateField} placeholder="교육 내용, 대상, 일정 등 필요한 정보를 입력하세요." maxLength={600} />
          </label>

          <div className="ai-media-upload__media-fields">
            <label>
              <span><FiLink aria-hidden="true" /> 영상 링크</span>
              <input name="mediaUrl" value={form.mediaUrl} onChange={updateField} placeholder="유튜브 또는 영상 주소" />
            </label>
            <label>
              <span><FiLink aria-hidden="true" /> 연결 페이지</span>
              <input name="targetUrl" value={form.targetUrl} onChange={updateField} placeholder="상담, 신청 또는 교재 상세 주소" />
            </label>
          </div>

          <div className="ai-media-upload__thumbnail">
            <label>
              <FiImage aria-hidden="true" />
              <span>대표 이미지 선택</span>
              <small>가로형 이미지 · 최대 1.5MB</small>
              <input type="file" accept="image/*" onChange={handleThumbnail} />
            </label>
            <div className={form.thumbnailUrl ? 'has-image' : ''} style={form.thumbnailUrl ? { backgroundImage: `url("${form.thumbnailUrl}")` } : undefined}>
              {!form.thumbnailUrl && <span>이미지 미리보기</span>}
            </div>
          </div>

          {error && <p className="ai-media-upload__error" role="alert">{error}</p>}
        </div>

        <footer>
          <button type="button" onClick={onClose}>취소</button>
          <button type="submit"><FiUploadCloud aria-hidden="true" /> 등록하기</button>
        </footer>
      </form>
    </div>,
    document.body,
  );
};

export default MediaUploadModal;
